export const MAINTENANCE_EVENT = "devplaner:maintenance";

export interface MaintenanceState {
    enabled: boolean;
    message: string;
    updatedAt: string | null;
}

interface PlatformStateResponse {
    maintenance?: unknown;
}

export const DEFAULT_MAINTENANCE_STATE: MaintenanceState = {
    enabled: false,
    message: "DevPlaner wird gerade gewartet. Bitte versuche es gleich erneut.",
    updatedAt: null,
};

function apiBase(): string {
    const configured = (process.env.NEXT_PUBLIC_API_URI ?? "").trim();
    const base = configured || "http://localhost:8000/api/v1";
    return base.endsWith("/") ? base.slice(0, -1) : base;
}

export function maintenanceStreamUrl(): string {
    return `${apiBase()}/meta/platform/stream`;
}

export function normalizeMaintenanceState(value: unknown): MaintenanceState {
    if (!value || typeof value !== "object") {
        return DEFAULT_MAINTENANCE_STATE;
    }

    const raw = value as Record<string, unknown>;
    const message = typeof raw.message === "string" && raw.message.trim()
        ? raw.message.trim().slice(0, 500)
        : DEFAULT_MAINTENANCE_STATE.message;

    return {
        enabled: raw.enabled === true,
        message,
        updatedAt: typeof raw.updatedAt === "string" ? raw.updatedAt : null,
    };
}

/**
 * Reads the same platform state that is changed by the DevPlaner Admin app.
 * A failed status request returns null so a short network interruption does
 * not incorrectly toggle the maintenance screen.
 */
export async function getMaintenanceState(signal?: AbortSignal): Promise<MaintenanceState | null> {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5_000);
    const abort = () => controller.abort();

    signal?.addEventListener("abort", abort, { once: true });

    try {
        const response = await fetch(`${apiBase()}/meta/platform`, {
            method: "GET",
            headers: {
                Accept: "application/json",
            },
            cache: "no-store",
            credentials: "omit",
            signal: controller.signal,
        });

        if (!response.ok) {
            return null;
        }

        const data = await response.json() as unknown;
        if (!data || typeof data !== "object") {
            return null;
        }

        return normalizeMaintenanceState((data as PlatformStateResponse).maintenance);
    } catch {
        return null;
    } finally {
        clearTimeout(timeout);
        signal?.removeEventListener("abort", abort);
    }
}
